/* eslint-disable import/named */
import { Action, configureStore } from '@reduxjs/toolkit';
import { delay, isEmpty, isPlainObject } from 'lodash/fp';
import { Middleware, Reducer, Store, combineReducers, compose } from 'redux';
import logger from 'redux-logger';
import {
	FLUSH,
	PAUSE,
	PERSIST,
	PURGE,
	REGISTER,
	createMigrate,
	persistReducer,
	persistStore,
} from 'redux-persist';
import type { Persistor } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import createSagaMiddleware, { Saga, SagaMiddlewareOptions } from 'redux-saga';

import { migrations as userMigrations } from '../modules/user/migrations';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MergedReducer = Reducer<unknown, Action<any>>;

type Reducers = Parameters<typeof persistReducer>[1];

export type StoreConfig = {
	enhancers?: Middleware[];
	reducers: Reducers;
	sagas?: Saga[];
};
type StoreConfigWithModules = StoreConfig & { modules: StoreConfig[] };

declare global {
	interface Window {
		__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
	}
}

const IS_SSR = typeof window === `undefined`;
const SUPPORTS_REDUX_DEV_TOOLS = !IS_SSR && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;

/**
 * Merges two StoreConfig
 */
const mergeStoreConfig = (
	configTarget: Required<StoreConfig>,
	configSource: StoreConfig
): Required<
	Omit<StoreConfig, 'reducers'> & {
		reducers: MergedReducer;
	}
> => {
	const { enhancers: enhancersTarget, reducers: reducersTarget, sagas: sagasTarget } = configTarget;
	const { enhancers: enhancersSource, reducers: reducersSource, sagas: sagasSource } = configSource;

	return {
		enhancers: enhancersSource ? enhancersTarget.concat(enhancersSource) : enhancersTarget,
		reducers: { ...reducersTarget, ...reducersSource } as MergedReducer,
		sagas: sagasSource ? sagasTarget.concat(sagasSource) : sagasTarget,
	};
};

function getMigrations() {
	return userMigrations;
}

const persistConfig: Parameters<typeof persistReducer>[0] = {
	key: 'root',
	storage,
	version: 0,
	whitelist: ['user', 'persisted', 'mobile', 'banners', 'cms'],
	migrate: createMigrate(getMigrations(), {
		debug:
			process.env.TARGET_ENV === 'accept' ||
			process.env.TARGET_ENV === 'nightly' ||
			process.env.NODE_ENV === 'development',
	}),
};
/**
 * Merges modules
 */
export const mergeStoreComponents = (
	storeConfig: StoreConfigWithModules
): Required<StoreConfig> => {
	console.assert(
		isPlainObject(storeConfig),
		`Store config should be plain object, received: ${JSON.stringify(storeConfig, null, 2)}`
	);
	const { enhancers = [], modules, reducers = {} as MergedReducer, sagas = [] } = storeConfig;
	const initialStore: Required<StoreConfig> = { enhancers, reducers, sagas };

	if (modules) {
		const combinedModules = modules.reduce<Required<StoreConfig>>(mergeStoreConfig, initialStore);

		return {
			...combinedModules,
			reducers: combineReducers(combinedModules.reducers),
		};
	}

	return initialStore;
};

/**
 * Creates redux store from modules
 */
export const createReduxStore = (
	storeConfig: StoreConfigWithModules,
	options: {
		initialState?: Record<string, unknown>;
		sagaOptions?: SagaMiddlewareOptions;
		delaySagaInitialization?: boolean;
	} = {}
): {
	store: Store;
	persistor: Persistor;
} => {
	const { enhancers, reducers, sagas } = mergeStoreComponents(storeConfig);

	const sagaMiddleware =
		!isEmpty(sagas) &&
		createSagaMiddleware({
			onError: (error) => {
				// eslint-disable-next-line no-console
				console.error('SAGA error:', error);
			},
			...options.sagaOptions,
		});

	const store = configureStore({
		reducer: persistReducer(persistConfig, reducers),
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware(
				process.env.NODE_ENV === 'development'
					? {
							serializableCheck: {
								ignoredActions: [
									`@@redux-form/BLUR`,
									`@@redux-form/CHANGE`,
									`@@redux-form/FOCUS`,
									`@@redux-form/REGISTER_FIELD`,
									`@@redux-form/UPDATE_SYNC_ERRORS`,
									FLUSH,
									PAUSE,
									PERSIST,
									PURGE,
									REGISTER,
								],
								ignoredActionPaths: ['payload.files'],
							},
						}
					: {
							serializableCheck: false,
							immutableCheck: false,
						}
			).concat(
				[
					sagaMiddleware,
					...enhancers,
					// console logger for dev environment without redux-dev-tools
					// @see https://github.com/LogRocket/redux-logger#usage
					process.env.NODE_ENV === 'development' && !IS_SSR && !SUPPORTS_REDUX_DEV_TOOLS && logger,
				].filter(Boolean) as Middleware[]
			),
		preloadedState: options.initialState || {},
	});

	if (sagaMiddleware) {
		const initSagas = () => sagas.forEach(sagaMiddleware.run);

		if (options.delaySagaInitialization) {
			delay(0, initSagas);
		} else {
			initSagas();
		}
	}

	const persistor = persistStore(store);

	return { store, persistor };
};

/**
 * Creates StoreConfig of module
 */
export const createReduxModule = (
	moduleName: string,
	reducer?: Reducer,
	sagas?: Saga[],
	enhancers?: Middleware[]
): Omit<StoreConfig, 'reducers'> & {
	reducers: Record<string, Reducer>;
} => ({
	reducers: reducer
		? {
				[moduleName]: reducer,
			}
		: {},
	sagas,
	enhancers,
});
